/**
 * Computes zero-aligned domains for dual Y-axes so both axes display
 * zero at the same visual (proportional) position.
 *
 * Algorithm:
 *  1. Ensure both domains include zero.
 *  2. Determine a target "zero fraction" — the fraction of the total visual
 *     range that lies below zero — that satisfies both axes without shrinking
 *     either axis's original data extent.
 *  3. Extend each axis so its zero fraction matches the target.
 *  4. Optionally apply symmetric padding (same fraction on both sides of both
 *     axes) which preserves the shared zero position.
 */
export function computeZeroAlignedDomains(
    y1Min: number,
    y1Max: number,
    y2Min: number,
    y2Max: number,
    padding = 0.05,
): { y1: [number, number]; y2: [number, number] } {
    const adjY1Min = Math.min(y1Min, 0);
    const adjY1Max = Math.max(y1Max, 0);
    const adjY2Min = Math.min(y2Min, 0);
    const adjY2Max = Math.max(y2Max, 0);

    const y1Pos = adjY1Max;
    const y1Neg = -adjY1Min;
    const y2Pos = adjY2Max;
    const y2Neg = -adjY2Min;

    const anyPositive = y1Pos > 0 || y2Pos > 0;
    const anyNegative = y1Neg > 0 || y2Neg > 0;

    if (!anyPositive || !anyNegative) {
        return {
            y1: [adjY1Min || -1, adjY1Max || 1],
            y2: [adjY2Min || -1, adjY2Max || 1],
        };
    }

    const bothPositive = y1Pos > 0 && y2Pos > 0;
    const bothNegative = y1Neg > 0 && y2Neg > 0;

    let targetFrac: number;

    if (bothPositive && bothNegative) {
        targetFrac = Math.max(
            y1Neg / (y1Pos + y1Neg),
            y2Neg / (y2Pos + y2Neg),
        );
    } else if (!bothPositive && !bothNegative) {
        targetFrac = 0.5;
    } else if (!bothNegative) {
        targetFrac =
            y1Neg > 0
                ? y1Neg / (y1Pos + y1Neg)
                : y2Neg / (y2Pos + y2Neg);
    } else {
        const y1Spans = y1Pos > 0 && y1Neg > 0;
        targetFrac = y1Spans
            ? y1Neg / (y1Pos + y1Neg)
            : y2Neg / (y2Pos + y2Neg);
    }

    targetFrac = Math.max(0.01, Math.min(0.99, targetFrac));

    const tY1 = Math.max(y1Neg / targetFrac, y1Pos / (1 - targetFrac)) || 1;
    const tY2 = Math.max(y2Neg / targetFrac, y2Pos / (1 - targetFrac)) || 1;

    let y1: [number, number] = [-(targetFrac * tY1), (1 - targetFrac) * tY1];
    let y2: [number, number] = [-(targetFrac * tY2), (1 - targetFrac) * tY2];

    if (padding > 0) {
        const r1 = y1[1] - y1[0];
        const r2 = y2[1] - y2[0];
        y1 = [y1[0] - padding * r1, y1[1] + padding * r1];
        y2 = [y2[0] - padding * r2, y2[1] + padding * r2];
    }

    return { y1, y2 };
}
